import { CircleCheck, LoaderCircle, TriangleAlert } from 'lucide-react';
import type { ImportOutcome } from '../../../queries';

/**
 * The import itself.
 *
 * There is no server-side import, so this is a create request per tournament and it is not
 * atomic. That is stated while it runs rather than discovered afterwards, and the outcome names
 * every rejection — a count of successes alone would leave the operator to work out which rows
 * to chase by comparing the file against the list.
 */
export function RunStep({
  total,
  done,
  outcome,
  error,
}: {
  total: number;
  done: number;
  outcome: ImportOutcome | null;
  error: string | null;
}) {
  const running = outcome === null && error === null;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  if (running) {
    return (
      <div className="py-8 text-center">
        <LoaderCircle className="mx-auto size-6 animate-spin text-felt-700" />
        <p className="mt-3 text-sm text-ink-950">
          Creating {done} of {total}…
        </p>
        <div className="mx-auto mt-4 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-ink-800/10">
          <div
            className="h-full bg-felt-700 transition-[width] duration-200"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="mt-4 text-xs text-ink-800/55">
          Closing this won&apos;t stop the import, but you won&apos;t see which rows were rejected.
        </p>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="py-8 text-center">
        <TriangleAlert className="mx-auto size-6 text-chip-500" />
        <p className="mt-3 text-sm text-ink-950">The import stopped.</p>
        <p className="mt-1 text-xs text-ink-800/60">{error}</p>
      </div>
    );
  }

  const failures = outcome?.failures ?? [];
  const created = outcome?.created ?? 0;
  const updated = outcome?.updated ?? 0;

  // Said apart, because they are different things to have happened to a schedule — and a run that
  // only updated is easy to read as a run that did nothing.
  const applied = [
    created > 0 ? `${created} tournament${created === 1 ? '' : 's'} created` : null,
    updated > 0 ? `${updated} updated` : null,
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="py-4 text-center">
        {failures.length === 0 ? (
          <CircleCheck className="mx-auto size-6 text-felt-700" />
        ) : (
          <TriangleAlert className="mx-auto size-6 text-gold-500" />
        )}
        <p className="mt-3 text-sm text-ink-950">
          {applied.length > 0 ? applied.join(', ') : 'Nothing changed'}
          {failures.length > 0
            ? `, ${failures.length} rejected — everything else has already been applied.`
            : '.'}
        </p>
      </div>

      {failures.length > 0 ? (
        <section className="overflow-hidden rounded-lg border border-ink-800/10">
          <header className="border-b border-ink-800/10 bg-felt-50/50 px-4 py-2">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800/50">
              Rejected by the server
            </h4>
          </header>
          <ul className="max-h-56 divide-y divide-ink-800/5 overflow-auto">
            {failures.map((failure) => (
              <li key={failure.internalId} className="flex items-start gap-2 px-4 py-2 text-xs">
                <span className="shrink-0 font-mono text-[11px] text-ink-800/60">
                  {failure.internalId}
                </span>
                <span className="min-w-0 flex-1 text-ink-800/80">{failure.message}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
